import { useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import { HTTPFormMeta } from "../../components/HTTPForm/Meta";
import APIErrors from "../../components/base/APIErrors";
import InboxActionFormBaseFields from "./InboxActionFormBaseFields";
import InboxActionFormFlowFields from "./InboxActionFormFlowFields";
import InboxActionFormHTTPFields from "./InboxActionFormHTTPFields";
import * as meta from "./InboxActionMeta";

export default function InboxActionForm({
  contact,
  formSubmit,
  success,
  submitted,
}) {
  const [currentStep, setCurrentStep] = useState("attrs");
  const [backendError, setBackendError] = useState("");
  const [backendValidationErrors, setBackendValidationErrors] = useState({});

  const onSubmit = (values, setSubmitting) => {
    const formData = { ...values };

    if (formData.action_type === "HTTP") {
      // Handle meta attributes if they exist
      if (formData.meta) {
        const metaAttributes = {};
        formData.meta.forEach(
          (attr) => (metaAttributes[attr.key] = attr.value)
        );
        formData.meta = metaAttributes;
      }

      if (formData.request) {
        formData.request_method = formData.request.method;
        formData.request_url = formData.request.url;

        if (Array.isArray(formData.request.headers)) {
          formData.request_headers = formData.request.headers.reduce(
            (acc, header) => {
              if (header.key && header.key.trim() !== "") {
                acc[header.key] = header.value || "";
              }
              return acc;
            },
            {}
          );
        } else {
          formData.request_headers = {};
        }

        if (Array.isArray(formData.request.params)) {
          formData.request_params = formData.request.params.reduce(
            (acc, param) => {
              if (param.key && param.key.trim() !== "") {
                acc[param.key] = param.value || "";
              }
              return acc;
            },
            {}
          );
        } else {
          formData.request_params = {};
        }

        if (
          formData.request.body_type &&
          (formData.request.body_type === "multipart/form-data" ||
            formData.request.body_type === "application/x-www-form-urlencoded")
        ) {
          if (Array.isArray(formData.request.body_params)) {
            const bodyParamsObj = formData.request.body_params.reduce(
              (acc, param) => {
                if (param.key && param.key.trim() !== "") {
                  acc[param.key] = param.value || "";
                }
                return acc;
              },
              {}
            );
            formData.request_body = JSON.stringify(bodyParamsObj);
          } else {
            formData.request_body = "{}";
          }
        } else {
          formData.request_body = formData.request.body || "";
        }

        formData.request_body_type = formData.request.body_type || "";

        delete formData.request;
      }
    } else if (formData.action_type === "FLOW") {
      delete formData.meta;
      delete formData.request;
    }

    formSubmit(
      formData,
      setSubmitting,
      setBackendValidationErrors,
      setBackendError
    );
  };

  const validationSchema = yup.object().shape({
    ...meta.InboxAction.schema,
    request: yup.object().when("action_type", {
      is: "HTTP",
      then: (schema) =>
        schema.shape({
          ...HTTPFormMeta.getSchema(),
        }),
      otherwise: (schema) => schema.optional(), // Make it optional for FLOW
    }),
  });

  const FIELDS = { ...meta.InboxAction.FIELDS, ...HTTPFormMeta.FIELDS };

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values, setSubmitting);
      }}
      enableReinitialize={true}
      initialValues={contact}
    >
      {(formik) => (
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className="my-4">
            <APIErrors
              APIError={backendError}
              APIValidationErrors={backendValidationErrors}
              fieldTranslations={FIELDS}
            />
          </div>

          {currentStep === "attrs" && (
            <InboxActionFormBaseFields
              formik={formik}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === "http" && formik.values.action_type === "HTTP" && (
            <InboxActionFormHTTPFields
              formik={formik}
              setCurrentStep={setCurrentStep}
              submitted={submitted}
              success={success}
            />
          )}
          {currentStep === "http" && formik.values.action_type === "FLOW" && (
            <InboxActionFormFlowFields
              formik={formik}
              setCurrentStep={setCurrentStep}
              submitted={submitted}
              success={success}
            />
          )}
        </form>
      )}
    </Formik>
  );
}
